import React, { useState, useEffect } from "react";
import { withNamespaces } from "react-i18next";
import {
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Paper,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Autocomplete,
  TextField,
  DialogActions,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Pagination,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Axios from "axios";
import Sidebar from "../../sidebar/sidebar";
import { companylogo } from "../../../assets/images";
import { BaseUrl } from "../../../Environment";
import moment from "moment";
import { Person } from "@mui/icons-material";
import { getSecureItem } from "../../../utilsFunctions";
import PdfReportCustomer from "./PdfReportCustomer";
import { getCompanyProfile } from "../../../api/index";
import excel from "../../../assets/images/excel.svg";
import pdf from "../../../assets/images/pdf.svg";
import { ExcelReport } from "../ExcelReport";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    cursor: "pointer",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const loanTableHeader = {
  loanId: "Loan ID",
  firstName: "Borrower",
  loanAmount: "Lent",
  totalCollectionAmount: "Collected",
  totalPenaltyApplied: "Penalty",
  balanceAmount: "Balance",
  pendingDues: "Remaining Dues",
};

const collectionTableHeader = {
  srNo: "Sr. No.",
  dueDate: "Due Date",
  collectedDate: "Collected Date",
  openingBalance: "Opening Balance",
  Installment: "Installment Amount",
  collectionAmount: "Collected",
  closingBalance: "Closing Balance",
  PenaltyApplied: "Penalty",
};

const CustomerWiseReport = () => {
  const loggedinUser = getSecureItem("loggedinUser");
  const columnWidths = [{ wch: 200 }, { wch: 200 }, { wch: 250 }];
  const currency = getSecureItem("country")?.symbol;
  const userType = loggedinUser?.userType || "";

  const fullReportRef = React.useRef(false);
  const [fullReport, setFullReport] = useState(false);
  const setFullReportSync = (val) => {
    fullReportRef.current = val;
    setFullReport(val);
  };

  const [loanData, setLoanData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [totalSummary, setTotalSummary] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [gotFullData, setGotFullData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [exportType, setExportType] = useState(null);

  const [loanId, setLoanId] = useState(null);
  const [collectionModal, setCollectionModal] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState("");
  const [loanCollectionData, setLoanCollectionData] = useState([]);
  const [loanSummary, setLoanSummary] = useState(null);

  const [showFirstModal, setShowFirstModal] = useState(true);
  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");

  const [page, setPage] = useState(1);
  const [loanStatus, setLoanStatus] = useState("Open");
  const [loanPaymentStatus, setLoanPaymentStatus] = useState("All");
  const [loanTenure, setLoanTenure] = useState("All");
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchData, setBranchData] = useState([]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const buildPayload = () => ({
    companyId: getSecureItem("loggedinUserCompany"),
    collectorId: loggedinUser?.userId,
    loanPaymentStatus,
    loanStatus,
    loanTenureType: loanTenure,
    limit: 20,
    page,
    fullReport: fullReportRef.current,
    userType,
    customerId,
    branchId: selectedBranch?.value || null,
  });

  const fetchBranches = async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId: loggedinUser.companyId },
        headers: { Authorization: "Bearer " + token },
      });
      const list =
        loggedinUser?.userType === "superAdmin"
          ? data?.data
          : data?.data?.filter((b) => b.branchId === loggedinUser?.branchId);
      setBranchData(
        list?.map((b) => ({ label: b.branchName, value: b.branchId })) || [],
      );
    } catch (err) {
      console.error("Fetch Branches Error:", err);
    }
  };

  const fetchCustomerList = async () => {
    try {
      const companyId = getSecureItem("loggedinUserCompany");
      const { data } = await Axios.get(
        `${BaseUrl}/customer/getAllCustomers?name=&companyId=${companyId}&branchId=${selectedBranch?.value || ""}`,
        { headers: { Authorization: "Bearer " + getSecureItem("token") } },
      );
      setCustomerList(
        data?.map((item) => ({
          label: item?.cust_id
            ? `${item.firstName} ${item.lastName} (${item.cust_id})`
            : `${item.firstName} ${item.lastName}`,
          value: item.customerId,
        })) || [],
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getLoansData = async () => {
    try {
      setLoading(true);
      const token = getSecureItem("token");
      const res = await Axios.post(
        `${BaseUrl}/loan/getLoanReport`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setLoanData(res?.data?.result || []);
      setTotalPages(Math.ceil((res?.data?.totalRecords || 0) / 20) || 1);
      if (fullReportRef.current) setGotFullData(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getLoansSummary = async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.post(
        `${BaseUrl}/loan/getLoansSummary`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setTotalSummary(data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getLoanCollections = async (id) => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.get(
        `${BaseUrl}/loanCollection/getLoanCollectionByLoanId/?loanId=${id}`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setLoanCollectionData(data?.data?.loanCollections || []);
      setLoanSummary(data?.data?.summary || null);
    } catch (err) {
      console.error(err);
    }
  };

  const getCompany = async () => {
    try {
      const compData = await getCompanyProfile();
      const {
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        AddressStreet,
        AddressLandmark,
        AddressCity,
        AddressState,
        AddressPincode,
      } = compData;
      setCompanyData({
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        address: {
          AddressStreet,
          AddressLandmark,
          AddressCity,
          AddressState,
          AddressPincode,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCompany();
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchCustomerList();
  }, [selectedBranch]);

  useEffect(() => {
    if (!customerId) return;
    getLoansData();
    getLoansSummary();
  }, [customerId, page, loanStatus, loanPaymentStatus, loanTenure, fullReport]);

  useEffect(() => {
    if (!loanData?.length) {
      setSortedData([]);
      return;
    }
    let arr = [...loanData];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        const aVal =
          sortConfig.key === "firstName"
            ? a?.customer?.firstName
            : a[sortConfig.key];
        const bVal =
          sortConfig.key === "firstName"
            ? b?.customer?.firstName
            : b[sortConfig.key];

        if (typeof aVal === "number" && typeof bVal === "number")
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        if (typeof aVal === "string" && typeof bVal === "string")
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        return 0;
      });
    }
    setSortedData(arr);
  }, [sortConfig, loanData]);

  useEffect(() => {
    if (!gotFullData || !sortedData.length) return;

    const fourBlockData = buildFourBlockData();

    if (exportType === "pdf") {
      PdfReportCustomer({
        sortedData,
        fourBlockData,
        loanStatus,
        loanPaymentStatus,
        loanTenure,
        companyData,
        selectedCustomer,
      });
    }

    if (exportType === "excel") {
      const rows = sortedData.map((item) => ({
        "Loan ID": item.loanId,
        Borrower:
          `${item?.customer?.firstName ?? ""} ${item?.customer?.lastName ?? ""}`.trim() +
          (item?.customer?.cust_id ? ` (${item.customer.cust_id})` : ""),
        "Lent Amount": item.loanAmount,
        "Collected Amount": item.totalCollected,
        "Penalty Applied": item.totalPenalty,
        "Balance Amount": item.outstandingBalance,
        "Remaining Dues": item.remainingDues,
        "Loan Tenure": item.loanTenure,
      }));
      ExcelReport({
        data: rows,
        fileName: "customer_wise_report.xlsx",
        sheetName: "Customer-Wise Report",
        columnWidths,
      });
    }

    setTimeout(() => {
      setExportType(null);
      setFullReportSync(false);
      setGotFullData(false);
    }, 100);
  }, [gotFullData]);

  const handleSortNew = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handlePdfClick = () => {
    setExportType("pdf");
    setFullReportSync(true);
  };

  const handleExcelClick = () => {
    setExportType("excel");
    setFullReportSync(true);
  };

  const buildFourBlockData = () => [
    { heading: "Total Lent", value: totalSummary?.totalLent ?? 0 },
    { heading: "Total Collected", value: totalSummary?.totalCollected ?? 0 },
    { heading: "Total Penalty", value: totalSummary?.totalPenalty ?? 0 },
    {
      heading: "Outstanding Balance",
      value: totalSummary?.outstandingBalance ?? 0,
    },
  ];

  const buildLoanSummaryData = () => [
    { heading: "Total Lent", value: loanSummary?.totalLent ?? 0 },
    { heading: "Total Collected", value: loanSummary?.totalCollected ?? 0 },
    { heading: "Total Penalty", value: loanSummary?.totalPenalty ?? 0 },
    {
      heading: "Outstanding Balance",
      value: loanSummary?.outstandingBalance ?? 0,
    },
  ];

  const LoanStatusFilter = [
    { label: "Open", value: "Open" },
    { label: "Closed", value: "Closed" },
    { label: "Cancelled", value: "Cancelled" },
    { label: "Replanned", value: "Replaned" },
    { label: "All", value: "All" },
  ];

  const PaymentStatusFilter = [
    { label: "Due", value: "Due" },
    { label: "OverDue", value: "OverDue" },
    { label: "All", value: "All" },
  ];

  const SequenceFilter = [
    { value: "onetime", label: "Onetime" },
    { value: "daily", label: "Daily" },
    ...(getSecureItem("loggedinUserCompany") == 2891
      ? [{ value: "ten_daily", label: "Ten_Daily" }]
      : []),
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "All", label: "All" },
  ];

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: "73px", height: "100vh" }}>
        <Grid container direction="column">
          <Grid item sx={{ backgroundColor: "#fff" }}>
            <div style={{ margin: 5 }}>
              <img src={companylogo} width={200} height={40} alt="logo" />
            </div>
          </Grid>

          <strong
            style={{
              textAlign: "center",
              fontFamily: "Lato",
              marginTop: 10,
              fontSize: 18,
            }}
          >
            Customer Wise Report
          </strong>

          <Grid
            item
            container
            paddingX={4}
            paddingY={2}
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={loanStatus}
                exclusive
                onChange={(_, val) => {
                  setPage(1);
                  setLoanStatus(val);
                }}
                size="small"
              >
                {LoanStatusFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={loanPaymentStatus}
                exclusive
                onChange={(_, val) => {
                  setPage(1);
                  setLoanPaymentStatus(val);
                }}
                size="small"
              >
                {PaymentStatusFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={loanTenure}
                exclusive
                onChange={(_, val) => {
                  setPage(1);
                  setLoanTenure(val);
                }}
                size="small"
              >
                {SequenceFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                style={{ width: 220 }}
                onClick={() => setShowFirstModal(true)}
                startIcon={<Person />}
              >
                Change Customer
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} paddingX={4} paddingY={2}>
            {buildFourBlockData().map(({ heading, value, color }) => (
              <Grid key={heading} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  elevation={3}
                  sx={{
                    minHeight: 130,
                    borderRadius: 3,
                    backgroundColor: color,
                  }}
                >
                  <CardHeader
                    title={<div style={{ fontSize: 15 }}>{heading}</div>}
                  />
                  <CardContent>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {(value ?? "0") + (currency ? " " + currency : "")}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {!loanId && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box paddingX={4} paddingY={2}>
                <p className="Fonts">NOTE: Click on record to see details</p>
              </Box>
              <Box paddingX={4} paddingY={2}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <p className="Fonts">Download Reports:</p>
                  <img
                    src={pdf}
                    alt="pdf"
                    className="pdficon"
                    onClick={handlePdfClick}
                  />
                  <img
                    src={excel}
                    alt="excel"
                    className="excelicon"
                    onClick={handleExcelClick}
                  />
                </Stack>
              </Box>
            </div>
          )}

          <Grid item container paddingX={4} paddingY={2}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {Object.keys(loanTableHeader).map((property) => (
                      <StyledTableCell key={property}>
                        <TableSortLabel
                          active={sortConfig.key === property}
                          direction={
                            sortConfig.key === property
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => handleSortNew(property)}
                        >
                          {loanTableHeader[property]}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((data, index) => (
                    <StyledTableRow
                      key={index}
                      onClick={() => {
                        setLoanId(data?.loanId);
                        setSelectedBorrower(
                          `${data?.customer?.firstName} ${data?.customer?.lastName}`,
                        );
                        getLoanCollections(data?.loanId);
                        setCollectionModal(true);
                      }}
                    >
                      <StyledTableCell>{data?.loanId}</StyledTableCell>
                      <StyledTableCell>
                        <Stack>
                          <div>
                            {data?.customer?.firstName}{" "}
                            {data?.customer?.lastName}
                          </div>
                          {data?.customer?.cust_id && (
                            <div>({data.customer.cust_id})</div>
                          )}
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell>{data?.loanAmount}</StyledTableCell>
                      <StyledTableCell>{data?.totalCollected}</StyledTableCell>
                      <StyledTableCell>{data?.totalPenalty}</StyledTableCell>
                      <StyledTableCell>{data?.outstandingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.remainingDues}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              variant="outlined"
              shape="rounded"
              size="large"
              style={{ marginTop: 10 }}
            />
          </Grid>
        </Grid>

        <Dialog
          open={showFirstModal}
          onClose={() => setShowFirstModal(false)}
          aria-labelledby="customer-select-dialog"
        >
          <DialogTitle id="customer-select-dialog">
            Customer-Wise Report
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Choose the Customer from here.
            </DialogContentText>
            <Autocomplete
              disablePortal
              disabled={loggedinUser?.company?.planId != 3}
              options={branchData}
              sx={{ width: 350, marginTop: 2 }}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Branch" />
              )}
            />
            <Autocomplete
              disablePortal
              options={customerList}
              sx={{ width: 350, marginTop: 2 }}
              onChange={(_, newValue) => {
                setCustomerId(newValue?.value || "");
                setSelectedCustomer(newValue?.label || "");
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Customer" />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => setShowFirstModal(false)}
            >
              Show Report
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      <Dialog
        open={collectionModal}
        fullWidth
        maxWidth="lg"
        onClose={() => {
          setLoanId(null);
          setCollectionModal(false);
        }}
        aria-labelledby="collection-dialog-title"
      >
        <DialogTitle id="collection-dialog-title">
          {`Loan Collection Table | Borrower's Name: ${selectedBorrower}, Loan ID: ${loanId}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} paddingY={2}>
            {buildLoanSummaryData().map(({ heading, value, color }) => (
              <Grid key={heading} item xs={12} sm={6} md={4} lg={3}>
                <Card
                  elevation={3}
                  sx={{
                    minHeight: 130,
                    borderRadius: 3,
                    backgroundColor: color,
                  }}
                >
                  <CardHeader
                    title={<div style={{ fontSize: 15 }}>{heading}</div>}
                  />
                  <CardContent>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>
                      {(value ?? "0") + (currency ? " " + currency : "")}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {loanId && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {Object.keys(collectionTableHeader).map((property) => (
                      <StyledTableCell key={property}>
                        <TableSortLabel
                          active={sortConfig.key === property}
                          direction={
                            sortConfig.key === property
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => handleSortNew(property)}
                        >
                          {collectionTableHeader[property]}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loanCollectionData.map((data, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {data?.dueDate
                          ? moment(data.dueDate).format("DD/MM/YYYY")
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>
                        {data?.collectedDate
                          ? moment(data.collectedDate).format("DD/MM/YYYY")
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>{data?.openingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.Installment}</StyledTableCell>
                      <StyledTableCell>
                        {data?.collectionAmount}
                      </StyledTableCell>
                      <StyledTableCell>{data?.closingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.PenaltyApplied}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setLoanId(null);
              setCollectionModal(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withNamespaces()(CustomerWiseReport);
